"use client";

import React from "react";
import { Mail, ArrowRight, Loader, MessageSquare } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
function ContactFormInner() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center animate-in fade-in duration-500">
        <div className="mb-4 flex size-12 items-center justify-center rounded-lg bg-accent/10 text-accent">
          <MessageSquare className="size-6" />
        </div>
        <h3 className="font-default mb-2 text-xl font-semibold text-foreground">
          {successMessage}
        </h3>
        <p className="mb-6 text-muted-foreground">
          Thank you for reaching out. Dustin looks forward to connecting with you.
        </p>
        <Button onClick={resetForm} variant="outline" className="rounded-md border-accent text-accent hover:bg-accent hover:text-accent-foreground">
          Send Another Message
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-2">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-medium uppercase tracking-wider text-xs">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="Dustin Royer" className="bg-transparent border-b border-x-0 border-t-0 border-border focus-visible:border-accent focus-visible:ring-0 px-0 text-foreground" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Please enter a valid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-muted-foreground font-medium uppercase tracking-wider text-xs">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="reader@example.com" className="bg-transparent border-b border-x-0 border-t-0 border-border focus-visible:border-accent focus-visible:ring-0 px-0 text-foreground" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="subject" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground font-medium uppercase tracking-wider text-xs">Inquiry Type</FormLabel>
            <FormControl>
              <Input placeholder="Speaking, Ministry, or Book Question?" className="bg-transparent border-b border-x-0 border-t-0 border-border focus-visible:border-accent focus-visible:ring-0 px-0 text-foreground" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-muted-foreground font-medium uppercase tracking-wider text-xs">Your Message</FormLabel>
            <FormControl>
              <textarea placeholder="How can we serve you today?" className="flex min-h-[120px] w-full resize-none bg-transparent px-0 py-2 text-base placeholder:text-muted-foreground focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50 border-b border-border focus-visible:border-accent transition-colors text-foreground" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
          {submitError}
        </div>}

      <div className="pt-4">
        <Button type="submit" disabled={isSubmitting} className="w-full rounded-md bg-primary py-6 text-base font-semibold text-primary-foreground hover:bg-primary/90 shadow-md transition-all">
          {isSubmitting ? <>
              <Loader className="mr-2 size-5 animate-spin" />
              Sending...
            </> : "Send Message"}
        </Button>
      </div>
    </Form>;
}
export default function LetsConnect() {
  return <section data-section-id="224719" id="get-in-touch" className="bg-background text-foreground py-24 md:py-32">
      <div className="container mx-auto px-6 max-w-6xl">
        <div className="grid gap-16 lg:grid-cols-12 lg:gap-8">
          
          {}
          <div className="lg:col-span-7 xl:col-span-8">
            <Card className="border-t-4 border-t-accent border-x-0 border-b-0 shadow-xl shadow-shadow-color/5 bg-card sm:p-4 rounded-md">
              <CardHeader className="pb-8">
                <CardTitle className="font-default text-4xl md:text-5xl text-foreground tracking-tight">
                  Get in Touch
                </CardTitle>
                <CardDescription className="text-lg text-muted-foreground mt-2 font-default">
                  Whether you have a question about a study guide or want to discuss a speaking engagement, I'd love to hear from you.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <FormProvider formKey="contact_form_contact" formId="contact_form_contact" sectionName="get-in-touch" defaultValues={{
                name: "",
                email: "",
                subject: "",
                message: ""
              }} validationRules={{
                name: {
                  required: true
                },
                email: {
                  required: true,
                  pattern: "email"
                },
                subject: {
                  required: false
                },
                message: {
                  required: true
                }
              }} submitText="Send Message" successMessage="Your message has been sent successfully!">
                  <ContactFormInner />
                </FormProvider>
              </CardContent>
            </Card>
          </div>

          {}
          <div className="lg:col-span-5 xl:col-span-4 flex flex-col justify-center space-y-12 lg:pl-10">
            <div>
              <h3 className="font-default text-2xl font-semibold text-foreground mb-6">
                Direct Contact
              </h3>
              
              <div className="space-y-8">
                <div className="group">
                  <h4 className="text-xs font-bold tracking-widest text-muted-foreground uppercase mb-2">
                    Ministry & Speaking
                  </h4>
                  <Link to="mailto:ministry@dustinroyerbooks.com" className="inline-flex items-center text-lg font-medium text-foreground hover:text-accent transition-colors border-b border-accent/30 hover:border-accent">
                    <Mail className="mr-3 size-5 text-accent" />
                    ministry@
                    <ArrowRight className="ml-2 size-4 opacity-0 -translate-x-2 transition-all group-hover:opacity-100 group-hover:translate-x-0" />
                  </Link>
                </div>

                <div className="group">
                  <h4 className="text-xs font-bold tracking-widest text-muted-foreground uppercase mb-2">
                    Reader Support
                  </h4>
                  <Link to="mailto:hello@dustinroyerbooks.com" className="inline-flex items-center text-lg font-medium text-foreground hover:text-accent transition-colors border-b border-accent/30 hover:border-accent">
                    <Mail className="mr-3 size-5 text-accent" />
                    hello@
                    <ArrowRight className="ml-2 size-4 opacity-0 -translate-x-2 transition-all group-hover:opacity-100 group-hover:translate-x-0" />
                  </Link>
                </div>
              </div>
            </div>

            <div className="rounded-md bg-muted/30 p-6 border border-border/50">
              <p className="text-sm text-muted-foreground leading-relaxed italic">
                "I value every reader and ministry partner. While I may not be able to respond instantly, I personally review every message and will get back to you as soon as possible."
              </p>
            </div>
          </div>

        </div>
      </div>
    </section>;
}